import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { logger } from '../lib/logger.js';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

// Last-resort fallback when a render-phase exception escapes a component.
// Strings are hardcoded (no i18n) so this still works if i18n is the thing
// that broke. Plug error tracking (Sentry/etc.) into componentDidCatch.
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    logger.error('[ErrorBoundary]', error, info.componentStack);
    // TODO: forward to error tracking service once configured.
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  private handleGoHome = (): void => {
    window.location.href = '/';
  };

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="flex items-center justify-center min-h-screen bg-bg-primary p-4">
        <div className="max-w-md w-full bg-bg-secondary border border-border-subtle rounded-xl p-6 text-center">
          <div className="flex justify-center mb-4">
            <div className="w-12 h-12 rounded-full bg-loss/20 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-loss" />
            </div>
          </div>
          <h1 className="text-xl font-semibold text-text-primary mb-2">
            Something went wrong
          </h1>
          <p className="text-sm text-text-secondary mb-6">
            The page hit an unexpected error. Try reloading — if it keeps happening, please contact support.
          </p>
          {this.state.error?.message && (
            <pre className="text-xs text-text-muted bg-bg-tertiary border border-border-subtle rounded-lg p-3 mb-6 text-left overflow-auto max-h-32">
              {this.state.error.message}
            </pre>
          )}
          <div className="flex gap-2 justify-center">
            <button
              onClick={this.handleReload}
              className="px-4 py-2 bg-accent-primary hover:bg-accent-primary/90 text-bg-primary font-medium rounded-lg transition-colors"
            >
              Reload
            </button>
            <button
              onClick={this.handleGoHome}
              className="px-4 py-2 bg-bg-tertiary hover:bg-bg-elevated text-text-primary font-medium rounded-lg transition-colors"
            >
              Go home
            </button>
          </div>
        </div>
      </div>
    );
  }
}
